import API_BASE_URL from '../config/apiConfig';
import { apiClient } from './authService';

export const getAnalyticsDashboard = async () => {
  try {
    const response = await apiClient(
      `${API_BASE_URL}/api/analytics/dashboard`,
      { method: 'GET' },
      true
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data?.message || data?.error || 'Failed to fetch analytics dashboard';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    if (data?.status && data.status !== 'success') {
      const errorMessage = data?.message || 'Failed to fetch analytics dashboard';
      const error = new Error(errorMessage);
      error.response = data;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Get analytics dashboard error:', error);
    if (!error.response && !error.isAuthError) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Fetch expanded admin analytics: growth trends, revenue, operational health,
 * bookings, maintenance, 7-day user growth series, and audit actions.
 * GET /api/analytics/admin/expanded  (ADMIN only)
 */
export const getExpandedAdminAnalytics = async () => {
  try {
    const response = await apiClient(
      `${API_BASE_URL}/api/analytics/admin/expanded`,
      { method: 'GET' },
      true
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data?.message || data?.error || 'Failed to fetch expanded admin analytics';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    if (data?.status && data.status !== 'success') {
      const errorMessage = data?.message || 'Failed to fetch expanded admin analytics';
      const error = new Error(errorMessage);
      error.response = data;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Get expanded admin analytics error:', error);
    if (!error.response && !error.isAuthError) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

export const getLandlordAnalytics = async () => {
  try {
    const response = await apiClient(
      `${API_BASE_URL}/api/analytics/landlord`,
      { method: 'GET' },
      true
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data?.message || data?.error || 'Failed to fetch landlord analytics';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    if (data?.status && data.status !== 'success') {
      const errorMessage = data?.message || 'Failed to fetch landlord analytics';
      const error = new Error(errorMessage);
      error.response = data;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Get landlord analytics error:', error);
    if (!error.response && !error.isAuthError) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

export const getLandlordRentAnalytics = async ({ year, month }) => {
  try {
    const params = new URLSearchParams();
    if (year !== undefined && year !== null) params.append('year', String(year));
    if (month !== undefined && month !== null) params.append('month', String(month));

    const response = await apiClient(
      `${API_BASE_URL}/api/analytics/landlord/rent?${params.toString()}`,
      { method: 'GET' },
      true
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data?.message || data?.error || 'Failed to fetch landlord rent analytics';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    if (data?.status && data.status !== 'success') {
      const errorMessage = data?.message || 'Failed to fetch landlord rent analytics';
      const error = new Error(errorMessage);
      error.response = data;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Get landlord rent analytics error:', error);
    if (!error.response && !error.isAuthError) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

export const getLandlordPropertyRentAnalytics = async ({ propertyId, year, month }) => {
  try {
    if (!propertyId) {
      const error = new Error('Property ID is required');
      error.status = 400;
      throw error;
    }

    const params = new URLSearchParams();
    if (year !== undefined && year !== null) params.append('year', String(year));
    if (month !== undefined && month !== null) params.append('month', String(month));

    const response = await apiClient(
      `${API_BASE_URL}/api/analytics/landlord/properties/${propertyId}/rent?${params.toString()}`,
      { method: 'GET' },
      true
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data?.message || data?.error || 'Failed to fetch property rent analytics';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    if (data?.status && data.status !== 'success') {
      const errorMessage = data?.message || 'Failed to fetch property rent analytics';
      const error = new Error(errorMessage);
      error.response = data;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Get property rent analytics error:', error);
    if (!error.response && !error.isAuthError) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Tenant rent-behavior score and breakdown.
 * GET /api/analytics/tenant/score?monthsBack=
 * @param {number} [monthsBack=12]
 */
export const getTenantScoreAnalytics = async (monthsBack = 12) => {
  const params = new URLSearchParams();
  params.set('monthsBack', String(monthsBack));

  try {
    const response = await apiClient(
      `${API_BASE_URL}/api/analytics/tenant/score?${params.toString()}`,
      { method: 'GET' },
      true
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data?.message || data?.error || 'Failed to fetch tenant score';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    if (data?.status && data.status !== 'success') {
      const errorMessage = data?.message || 'Failed to fetch tenant score';
      const error = new Error(errorMessage);
      error.response = data;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Get tenant score analytics error:', error);
    if (!error.response && !error.isAuthError) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

export const getAgentAnalytics = async () => {
  try {
    const response = await apiClient(
      `${API_BASE_URL}/api/analytics/agent`,
      { method: 'GET' },
      true
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data?.message || data?.error || 'Failed to fetch agent analytics';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    if (data?.status && data.status !== 'success') {
      const errorMessage = data?.message || 'Failed to fetch agent analytics';
      const error = new Error(errorMessage);
      error.response = data;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Get agent analytics error:', error);
    if (!error.response && !error.isAuthError) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Fetch BnB Host analytics
 * GET /api/analytics/bnb-host
 */
export const getBnbHostAnalytics = async ({ month, year } = {}) => {
  try {
    const params = new URLSearchParams();
    if (month != null) params.append('month', String(month));
    if (year != null)  params.append('year',  String(year));
    const qs = params.toString();
    const response = await apiClient(
      `${API_BASE_URL}/api/analytics/bnb-host${qs ? `?${qs}` : ''}`,
      { method: 'GET' },
      true
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data?.message || data?.error || 'Failed to fetch BNB host analytics';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    if (data?.status && data.status !== 'success') {
      const errorMessage = data?.message || 'Failed to fetch BNB host analytics';
      const error = new Error(errorMessage);
      error.response = data;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Get BNB host analytics error:', error);
    if (!error.response && !error.isAuthError) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Fetch BnB Host revenue analytics.
 * GET /api/analytics/bnb-host/rent?year=&month=
 */
export const getBnbHostRentAnalytics = async ({ year, month } = {}) => {
  try {
    const params = new URLSearchParams();
    if (year !== undefined && year !== null) params.append('year', String(year));
    if (month !== undefined && month !== null) params.append('month', String(month));
    const qs = params.toString();

    const response = await apiClient(
      `${API_BASE_URL}/api/analytics/bnb-host/rent${qs ? `?${qs}` : ''}`,
      { method: 'GET' },
      true
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data?.message || data?.error || 'Failed to fetch BNB host rent analytics';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    if (data?.status && data.status !== 'success') {
      const errorMessage = data?.message || 'Failed to fetch BNB host rent analytics';
      const error = new Error(errorMessage);
      error.response = data;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Get BNB host rent analytics error:', error);
    if (!error.response && !error.isAuthError) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};
